import type { RouteLocationRaw } from 'vue-router';
import {
  ChatAttentionReason,
  ChatBackend,
  SigningAttentionReason,
  UserAttentionType,
  type UserAttentionSearchResult,
} from '@/api';
import { signingRequestRoute } from '@/router/links';

/** One entry of the user's attention list — a chat thread or a signing turn. */
export type UserAttentionItem = UserAttentionSearchResult['results'][number];
export type ChatAttentionItem = Extract<UserAttentionItem, { type: 'chat_thread' }>;
export type SigningAttentionItem = Extract<UserAttentionItem, { type: 'signing' }>;

// A subset of GaiaStatusIndicator's statuses (kept here: types from .vue files are any to eslint).
export type AttentionStatus = 'alert' | 'risk' | 'done' | 'pending';

export function isChatAttention(item: UserAttentionItem): item is ChatAttentionItem {
  return item.type === 'chat_thread';
}

export function isSigningAttention(item: UserAttentionItem): item is SigningAttentionItem {
  return item.type === 'signing';
}

const CHAT_STATUS: Record<ChatAttentionReason, AttentionStatus> = {
  [ChatAttentionReason.INTERRUPT]: 'alert',
  [ChatAttentionReason.FAILED]: 'risk',
  [ChatAttentionReason.DONE]: 'done',
};

const CHAT_LABEL: Record<ChatAttentionReason, string> = {
  [ChatAttentionReason.INTERRUPT]: 'Waiting for your answer',
  [ChatAttentionReason.DONE]: 'Finished while you were away',
  [ChatAttentionReason.FAILED]: 'This run failed',
};

const SIGNING_LABEL: Record<SigningAttentionReason, string> = {
  [SigningAttentionReason.SIGN]: 'Waiting for your signature',
  [SigningAttentionReason.APPROVE]: 'Waiting for your approval',
};

/** The verb on the provider-page button: signers sign, approvers approve. */
export const SIGNING_ACTION_VERB: Record<SigningAttentionReason, string> = {
  [SigningAttentionReason.SIGN]: 'Sign',
  [SigningAttentionReason.APPROVE]: 'Approve',
};

export const ATTENTION_TYPE_LABEL: Record<UserAttentionType, string> = {
  [UserAttentionType.CHAT_THREAD]: 'Chat',
  [UserAttentionType.SIGNING]: 'Signing',
};

export function attentionTitle(item: UserAttentionItem): string {
  const title = isChatAttention(item) ? item.title : item.signing_request.name;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
  return title?.trim() || (isChatAttention(item) ? 'Untitled chat' : 'Untitled request');
}

export function attentionStatus(item: UserAttentionItem): AttentionStatus {
  return isChatAttention(item) ? CHAT_STATUS[item.reason] : 'pending';
}

export function attentionStatusLabel(item: UserAttentionItem): string {
  return isChatAttention(item) ? CHAT_LABEL[item.reason] : SIGNING_LABEL[item.reason];
}

/** Newest activity — what the backend orders by. */
export function attentionUpdatedAt(item: UserAttentionItem): string {
  return item.updated_at ?? item.created_at;
}

/**
 * Where a row opens, or null when it has nowhere to go in this app: templating
 * threads live in the Word add-in and review-project threads inside their
 * project page, which the attention item cannot address.
 */
export function attentionTarget(item: UserAttentionItem): RouteLocationRaw | null {
  if (isSigningAttention(item)) {
    return signingRequestRoute(item.signing_request.uid);
  }
  if (item.backend !== ChatBackend.GENERAL) {
    return null;
  }
  return { name: 'home', query: { thread_uid: item.thread_uid } };
}
